import type {
  Container,
  ContainerItem,
  ContainerItemLookupResponse,
  Item,
} from "@egalregal/shared";
import type {
  BarcodeLookupResponse,
  OwnCatalogEntry,
  SharedCatalogEntry,
} from "../../shared/api/requests";
import { requests } from "../../shared/api";
import { NotFoundError, UnreachableError } from "../../shared/api/errors";
import type { ScanOutcome } from "../../shared/store/scanHistory";
import {
  readableContainers,
  useAppStore,
  type ScannedContainerResult,
} from "../../shared/store/app";
import { cachedCatalogs, readCachedContainers } from "../search/catalogStore";
import { entryIdOfContainer } from "../catalogs/entryLabel";
import { log } from "../../logger";
import { placementOptionsFor, type PlacementOption } from "./placements";
import { resolveMirroredCode } from "./resolveItem";
import type { DisplayPayload, ScanPayload } from "./payload";

/**
 * The one resolution sequence behind every scanner variant (scan-resolution
 * 01). It is deliberately domain-only: `idle` / `resolving` and the product's
 * `looking` / `manual` phases are view state the presets own, never results of
 * resolving a scan.
 *
 * The module is pure at the seam: {@link resolveScan} takes injected lookups and
 * does no React, navigation or history writes. {@link createResolveDeps} wires
 * the lookups to the offline read cache and the request client for production
 * callers (tickets 02–03).
 */

/** Where a resolved target came from: the browser read cache or the server. */
export type ResolutionSource = "cache" | "network";

/** A readable container, from the live slice, the read cache or the server. */
export interface ContainerResolutionFound {
  kind: "container";
  container: Container;
  source: ResolutionSource;
}

/** A container in the trash: a target, but not one the scanner can open. */
export interface TrashResolution {
  kind: "trash";
  container: Container;
}

/** A mirrored item and the placements that locate it. */
export interface ItemResolution {
  kind: "item";
  item: Item;
  placements: PlacementOption[];
  source: ResolutionSource;
  /**
   * The placement inside the requested container, for the `container-add`
   * loop's quantity stepper. Only a container-scoped lookup sets it.
   */
  placementHere?: ContainerItem;
}

/** Every target a label can resolve to (the access core's `ContainerResolution` is a different type). */
export type ResolvedScanTarget = ContainerResolutionFound | TrashResolution | ItemResolution;

/**
 * What a label lookup can answer: a domain target, a definite miss (the server
 * says the caller may not read it) or no answer at all (the request never
 * reached a server).
 */
export type LabelLookup = ResolvedScanTarget | "not-found" | "unreachable";

/** What a product lookup can answer: a provider preview, a mirrored item, or a failure. */
export type ProductLookup =
  | { kind: "product"; preview: BarcodeLookupResponse }
  | ItemResolution
  | "not-found"
  | "unreachable";

/** What a container-scoped lookup can answer (the `container-add` variant). */
export type InContainerLookup = ContainerItemLookupResponse | "not-found" | "unreachable";

/** Why a product scan fell back to the preset's manual form. */
export type ProductFallbackReason = "miss" | "offline";

/** Why a dialog preset left its lookup loop for the manual/error branch. */
export type ManualFallback = "miss" | "offline" | "unknown" | "payload";

/**
 * The domain result of resolving one scan. Presets map this to their view and
 * write their own history outcome through {@link outcomeForResolution}.
 */
export type ScanResolution =
  | ResolvedScanTarget
  | { kind: "unknown-label"; code: string }
  | { kind: "offline-uncached"; code: string }
  | {
      kind: "product";
      code: string;
      /** The provider preview, when one was found. */
      preview?: BarcodeLookupResponse;
      /** The manual fallback reason, when there is no preview. */
      reason?: ProductFallbackReason;
    }
  | { kind: "payload"; payload: DisplayPayload };

/**
 * The lookups a resolver call needs. Each scanner variant injects the ones it
 * owns: `identify` and `global` resolve labels, `container-add` resolves a
 * barcode inside a container, `item-create` resolves a product barcode.
 */
export interface ResolveDeps {
  /** Cache-first label resolution. */
  resolveLabel(code: string): Promise<LabelLookup>;
  /** Cache-first product resolution (mirror, then provider). */
  lookupProduct(code: string): Promise<ProductLookup>;
  /** Container-scoped lookup; `external` disables the provider step for labels. */
  lookupInContainer?(
    containerId: string,
    barcode: string,
    options: { external: boolean },
  ): Promise<InContainerLookup>;
}

/** Per-call context; `containerId` selects the `container-add` resolution. */
export interface ResolveOptions {
  /** Resolve the barcode inside this container instead of globally. */
  containerId?: string;
}

/** The cached catalog entry list (the `entries` store of the read cache). */
export interface CachedCatalogEntries {
  entries: readonly OwnCatalogEntry[];
  sharedWithMe: readonly SharedCatalogEntry[];
}

/**
 * The real seams behind {@link createResolveDeps}. Every one is a function so
 * the app store and IndexedDB are read when a scan resolves, and a test can
 * replace any of them with a fake.
 */
export interface ScanResolverSources {
  /** The readable container union currently loaded (entry slice until it loads). */
  containers(): readonly Container[];
  /** The trash slice currently loaded. */
  trash(): readonly Container[];
  /** Barcode/UUID lookup against the item mirror. */
  mirroredItem(code: string): Item | undefined;
  /** An item's placement options, for a mirrored item. */
  placements(itemId: string): PlacementOption[];
  /** The persisted readable container union (offline read cache). */
  cachedContainers(): Promise<readonly Container[]>;
  /** The persisted catalog entry list, or null when none was cached. */
  cachedEntries(): Promise<CachedCatalogEntries | null>;
  /**
   * Read-enforced container lookup; resolves the 404/unreachable distinction.
   * Any other failure is rethrown, never turned into "offline".
   */
  fetchContainer(id: string): Promise<ScannedContainerResult>;
  /** Provider preview for a barcode no stored item matches. */
  fetchProduct(code: string): Promise<BarcodeLookupResponse>;
  /** Container-scoped lookup (the `container-add` loop). */
  fetchInContainer?(
    containerId: string,
    barcode: string,
    options: { external: boolean },
  ): Promise<ContainerItemLookupResponse>;
}

/**
 * Resolve one classified scan into a domain result.
 *
 * A label resolves through the injected label lookup; a product resolves
 * through the provider lookup (or the container-scoped lookup when a
 * `containerId` is given); a URL, Wi-Fi or text payload is its own result and
 * never asks a lookup. Resolution itself owns no cache, view or history: those
 * live in the real deps and the presets.
 *
 * @param payload the classified scan payload.
 * @param deps the injected lookups.
 * @param options per-call context (`containerId` for the container-add loop).
 * @returns the domain resolution, never `idle` or `resolving`.
 */
export async function resolveScan(
  payload: ScanPayload,
  deps: ResolveDeps,
  options: ResolveOptions = {},
): Promise<ScanResolution> {
  if (payload.kind === "url" || payload.kind === "wifi" || payload.kind === "text") {
    return { kind: "payload", payload };
  }
  const code = payload.kind === "label" ? payload.uuid : payload.code;
  if (options.containerId !== undefined && deps.lookupInContainer) {
    return inContainerResolution(
      code,
      await deps.lookupInContainer(options.containerId, code, {
        external: payload.kind === "product",
      }),
    );
  }
  if (payload.kind === "label") return labelResolution(code, await deps.resolveLabel(code));
  return productResolution(code, await deps.lookupProduct(code));
}

/**
 * The history outcome snapshot for a resolved scan (ADR-0041). The snapshot is
 * a display fallback only: opening a history entry re-resolves its target.
 * Trash is still a container target; unknown labels, offline-uncached codes and
 * product previews have no target yet (`none`).
 *
 * @param result the domain resolution.
 * @returns the outcome to store on the scan's history entry.
 */
export function outcomeForResolution(result: ScanResolution): ScanOutcome {
  if (isResolvedTarget(result)) return targetOf(result);
  switch (result.kind) {
    case "payload":
      return { kind: "payload" };
    case "unknown-label":
    case "offline-uncached":
    case "product":
      return { kind: "none" };
    default:
      // A new result kind must extend the history mapping, not fall through.
      return assertNever(result);
  }
}

/**
 * The dialog presets' one manual-fallback decision (scan-resolution 03). It
 * replaces the three hand-written 404 branches: a resolution with nothing the
 * dialog can act on — a provider miss, an offline lookup, an unknown or
 * uncached label, or a non-barcode payload — sends the preset to its manual
 * form (or, for `identify`, its "not found" message).
 *
 * @param result the resolver's domain result.
 * @returns the fallback reason, or null when the result carries a target.
 */
export function manualFallbackOf(result: ScanResolution): ManualFallback | null {
  if (isResolvedTarget(result)) return null;
  switch (result.kind) {
    case "product":
      // A provider preview is actionable; without one the preset creates by hand.
      return result.preview ? null : (result.reason ?? "miss");
    case "unknown-label":
      return "unknown";
    case "offline-uncached":
      return "offline";
    case "payload":
      return "payload";
    default:
      // A new result kind must extend the fallback decision, not fall through.
      return assertNever(result);
  }
}

/** Whether a resolution carries a target the presets can act on. */
function isResolvedTarget(result: ScanResolution): result is ResolvedScanTarget {
  return result.kind === "container" || result.kind === "trash" || result.kind === "item";
}

/** The one history-target mapping for a resolved scan. */
function targetOf(result: ResolvedScanTarget): ScanOutcome {
  return result.kind === "item"
    ? { kind: "item", id: result.item.id, label: result.item.name }
    : { kind: "container", id: result.container.id, label: result.container.name };
}

/**
 * Build the production resolver deps, with the browser read cache consulted
 * before the network:
 *
 * 1. live container (readable union in the app store)
 * 2. trashed container
 * 3. mirrored item (the item/placement mirror)
 * 4. cached label (persisted container union, checked against the persisted
 *    catalog entry list)
 * 5. the network
 *
 * A network 404 is a definite miss; an unreachable server keeps the earlier
 * steps' answer and otherwise reports `offline-uncached`, so a valid label is
 * never shown as unknown because the connection dropped.
 *
 * @param overrides replace any source seam (tests pass fakes; production none).
 * @returns the deps {@link resolveScan} consumes.
 */
export function createResolveDeps(
  overrides: Partial<ScanResolverSources> = {},
): ResolveDeps {
  const sources: ScanResolverSources = { ...DEFAULT_SOURCES, ...overrides };
  const fetchInContainer = sources.fetchInContainer;

  return {
    resolveLabel: (code) => resolveLabelFromSources(code, sources),
    lookupProduct: (code) => lookupProductFromSources(code, sources),
    ...(fetchInContainer
      ? { lookupInContainer: (containerId, barcode, options) =>
          lookupInContainerFromSources(fetchInContainer, containerId, barcode, options) }
      : {}),
  };
}

/** Production sources: the app store, the offline read cache and the API client. */
const DEFAULT_SOURCES: ScanResolverSources = {
  containers: () => readableContainers(useAppStore.getState()),
  trash: () => useAppStore.getState().trash,
  mirroredItem: (code) => resolveMirroredCode(code),
  placements: (itemId) =>
    placementOptionsFor(readableContainers(useAppStore.getState()), itemId),
  cachedContainers: () =>
    typeof indexedDB === "undefined" ? Promise.resolve([]) : readCachedContainers(),
  cachedEntries: () => cachedCatalogs(),
  fetchContainer: (id) => useAppStore.getState().resolveScannedContainer(id),
  fetchProduct: (code) => requests.items.lookupBarcode(code),
  fetchInContainer: (containerId, barcode, options) =>
    requests.items.lookupInContainer(containerId, barcode, { external: options.external }),
};

/**
 * The cache-first label chain: live container, trash, mirrored item, cached
 * label, then the network. An unreachable server returns `unreachable` rather
 * than a miss, so the caller can distinguish it from a 404. Any other failure
 * (a 403, a 500) propagates to the caller's own lookup-error path.
 */
async function resolveLabelFromSources(
  code: string,
  sources: ScanResolverSources,
): Promise<LabelLookup> {
  const live = sources.containers().find((candidate) => candidate.id === code);
  if (live) return { kind: "container", container: live, source: "cache" };
  const trashed = sources.trash().find((candidate) => candidate.id === code);
  if (trashed) return { kind: "trash", container: trashed };
  const mirrored = sources.mirroredItem(code);
  if (mirrored) return itemResolution(mirrored, "cache", sources);
  const cached = await cachedLabel(code, sources);
  if (cached) return cached;
  // No point asking a network the browser knows is gone: the typed offline
  // signal and a failed request mean the same thing to the caller.
  if (isOffline()) return "unreachable";
  const fetched = await sources.fetchContainer(code);
  if (fetched.kind === "found") {
    return { kind: "container", container: fetched.container, source: "network" };
  }
  return fetched.kind === "not-found" ? "not-found" : "unreachable";
}

/** The cache-first product chain: the item mirror, then the provider preview. */
async function lookupProductFromSources(
  code: string,
  sources: ScanResolverSources,
): Promise<ProductLookup> {
  const mirrored = sources.mirroredItem(code);
  if (mirrored) return itemResolution(mirrored, "cache", sources);
  // A known-offline browser must not surface a provider error as a lookup miss.
  if (isOffline()) return "unreachable";
  try {
    return { kind: "product", preview: await sources.fetchProduct(code) };
  } catch (err) {
    return lookupFailure("product", err);
  }
}

/** A container-scoped lookup, with the same typed-error mapping as the others. */
async function lookupInContainerFromSources(
  fetchInContainer: NonNullable<ScanResolverSources["fetchInContainer"]>,
  containerId: string,
  barcode: string,
  options: { external: boolean },
): Promise<InContainerLookup> {
  try {
    return await fetchInContainer(containerId, barcode, options);
  } catch (err) {
    return lookupFailure("in-container", err);
  }
}

/** Whether the browser has already told the app there is no connection. */
function isOffline(): boolean {
  return typeof navigator !== "undefined" && navigator.onLine === false;
}

/**
 * Translate a request failure into the lookup union. A 404 is a definite miss;
 * a transport rejection has no answer; anything else (a 500, an unexpected
 * body) is rethrown so it is not silently reported as "offline".
 */
function lookupFailure(scope: "product" | "in-container", err: unknown): "not-found" | "unreachable" {
  if (err instanceof NotFoundError) return "not-found";
  if (err instanceof UnreachableError) return "unreachable";
  log.warn({ err, scope }, "[scan] Lookup failed unexpectedly");
  throw err;
}

/**
 * The offline read-cache half of label resolution. A container row persisted by
 * the last readable-union sync answers offline; when a catalog entry list was
 * cached too, the row must still belong to one of those entries, so a revoked
 * entry's leftover cannot be surfaced. With no cached entry list the row is the
 * only evidence and is trusted (freshness is the last successful load, ADR-0005).
 *
 * @param code the scanned label UUID.
 * @param sources the resolver's injected seams.
 * @returns the cached container result, or undefined when the cache cannot answer.
 */
async function cachedLabel(
  code: string,
  sources: ScanResolverSources,
): Promise<ContainerResolutionFound | undefined> {
  let containers: readonly Container[] = [];
  let catalogs: CachedCatalogEntries | null = null;
  try {
    [containers, catalogs] = await Promise.all([
      sources.cachedContainers(),
      sources.cachedEntries(),
    ]);
  } catch (err) {
    // A cache failure must never block a resolution the network could answer.
    log.warn({ err, code }, "[scan] Read cache unavailable; resolving from the network");
    return undefined;
  }
  const container = containers.find((candidate) => candidate.id === code);
  if (!container) return undefined;
  if (catalogs && !belongsToCachedEntry(containers, catalogs, code)) return undefined;
  return { kind: "container", container, source: "cache" };
}

/**
 * Whether a cached container is still readable through the cached catalog
 * entries: its root must be one of an own or shared entry's roots.
 */
function belongsToCachedEntry(
  containers: readonly Container[],
  catalogs: CachedCatalogEntries,
  containerId: string,
): boolean {
  return (
    entryIdOfContainer(containers, catalogs.entries, catalogs.sharedWithMe, containerId) !==
    undefined
  );
}

/** A mirrored item plus its placements, tagged with the source that found it. */
function itemResolution(
  item: Item,
  source: ResolutionSource,
  sources: ScanResolverSources,
): ItemResolution {
  return { kind: "item", item, placements: sources.placements(item.id), source };
}

/** Map a label lookup answer onto the domain result. */
function labelResolution(code: string, found: LabelLookup): ScanResolution {
  if (found === "not-found") return { kind: "unknown-label", code };
  if (found === "unreachable") return { kind: "offline-uncached", code };
  return found;
}

/** Map a product lookup answer onto the domain result. */
function productResolution(code: string, found: ProductLookup): ScanResolution {
  if (found === "not-found") return { kind: "product", code, reason: "miss" };
  if (found === "unreachable") return { kind: "product", code, reason: "offline" };
  if (found.kind === "item") return found;
  return { kind: "product", code, preview: found.preview };
}

/**
 * Map a container-scoped lookup onto the domain result. A known item is the
 * target; an external preview is a product preview; anything else is the
 * preset's manual create-and-place path (a product result without a preview).
 * The in-container quantity stays with the preset's own loop.
 */
function inContainerResolution(code: string, found: InContainerLookup): ScanResolution {
  if (found === "not-found") return { kind: "product", code, reason: "miss" };
  if (found === "unreachable") return { kind: "product", code, reason: "offline" };
  if (found.known) {
    return {
      kind: "item",
      item: found.known,
      placements: [],
      source: "network",
      ...(found.placementHere ? { placementHere: found.placementHere } : {}),
    };
  }
  if (found.external) return { kind: "product", code, preview: found.external };
  return { kind: "product", code, reason: "miss" };
}

/** Compile-time exhaustiveness check for a result union. */
function assertNever(value: never): never {
  throw new Error(`Unhandled scan resolution: ${JSON.stringify(value)}`);
}
